import { observable, action, computed } from 'mobx';
const Validator = require('validatorjs');
import userSharedFormsValidator from '../services/validations/userSharedFormsValidator';
import passwordMatchValidator from '../services/validations/passwordMatchValidator';
import agent from '../services/web/agent';
import AuthenticationProviderType from '../services/enums/authenticationProviderType';
import oidcStore from './oidcStore';

class AddEditUserStore {

    formErrors = [];
    formMode = '';
    @observable creatingUser;
    @observable updatingUser;
    @observable isAddUserError;
    @observable addUserErrorMessage;

    @observable inProgress;
    @observable userNameValidationMessage;
    @observable firstNameValidationMessage;
    @observable lastNameValidationMessage;
    @observable emailValidationMessage;
    @observable passwordValidationMessage;
    @observable passwordConfirmedValidationMessage;
    @observable registerErrorMessage;
    @observable isRegisterError = null;
    @observable userEditDialogForm;

    @action setUserEditDialogForm(userEditDialogForm) {

        this.userEditDialogForm = userEditDialogForm;
    }

    @computed get organizationUnitsFormComplete() {

        return this.values.teamIds && this.values.teamIds.length > 0 ? true : false;
    }

    @observable values = {
        id: null,
        firstName: '',
        lastName: '',
        userName: '',
        email: '',
        password: '',
        confirmedPassword: '',
        teamIds: [],
        jobTitle: '',
        phoneNumber: '',
        cellPhoneNumber: '',
        other: '',
        country: '',
        active: true,
        expirationDate: '',
        allowedIpList: [],
        localeId: '',
        roleIds: [],   
        authenticationProviderId: -1,
        authenticationProviderType: '', 
        authenticationProviderFormatted: '',
        tenant: '',
        twoFAExemption: false,
		accessToUi:true
    };

    validateFirstName() {

        const validation = userSharedFormsValidator.validateFirstName(this.values.firstName);

        this.firstNameValidationMessage = validation.errors.first('firstName');

        this.formErrors.push(validation.errorCount);
    }

    validateLastName() {

        const validation = userSharedFormsValidator.validateLastName(this.values.lastName);

        this.lastNameValidationMessage = validation.errors.first('lastName');

        this.formErrors.push(validation.errorCount);
    }

    validateUserName() {

        const validation = userSharedFormsValidator.validateUserName(this.values.userName);

        this.userNameValidationMessage = validation.errors.first('userName');

        this.formErrors.push(validation.errorCount);
    }

    validateEmail() {

        const validation = userSharedFormsValidator.validateEmail(this.values.email);

        this.emailValidationMessage = validation.errors.first('email');

        this.formErrors.push(validation.errorCount);
    }

    validatePassword() {

        const result = passwordMatchValidator.validatePassword(this.values.password);

        this.passwordValidationMessage = result.passwordValidationMessage;

        this.formErrors.push(result.validation.errorCount);
    }

    validateMatchPassword() {

        const result = passwordMatchValidator.validateMatchPassword(this.values.password, this.values.confirmedPassword);

        this.passwordConfirmedValidationMessage = result.passwordValidationMessage;

        this.formErrors.push(result.validation.errorCount);
    }

    validateTeamsIds() {

        const data = {
            teamIds: this.values.teamIds.length
        };
        
        const rules = {
            teamIds: 'min:1'
        };

        const validation = new Validator(data, rules);
        validation.passes();
        
        this.formErrors.push(validation.errorCount);
    }

    @action validateAddForm() { 

        this.formErrors = [];
        this.validateFirstName();
        this.validateLastName();
        this.validateUserName();
        this.validateEmail();
        this.validateTeamsIds();

        if (this.isLocalAuthenticationType() === true) {
            
            this.validateMatchPassword();
        }
        
        if (this.formErrors.filter(valid => valid > 0).length > 0) {
            
            return false;   
        }

        return true;
    }

    @action validateEditForm() { 

        this.formErrors = [];
        this.validateFirstName();
        this.validateLastName();
        this.validateEmail();
        this.validateTeamsIds();
        
        if (this.formErrors.filter(valid => valid > 0).length > 0) {
            
            return false;   
        }

        return true;
    }

    reset() {

        this.values.id = null;
        this.values.firstName = '';
        this.values.lastName = '';
        this.values.userName = '';
        this.values.email = '';
        this.values.password = '';
        this.values.confirmedPassword = '';
        this.values.teamIds = [];

        this.values.jobTitle = '';
        this.values.phoneNumber = '';
        this.values.cellPhoneNumber = '';
        this.values.other = '';
        this.values.country = '';
        this.values.active = true; //default
        this.values.expirationDate = '';
        this.values.allowedIpList = [];
        this.values.localeId = '';
        this.values.roleIds = [];
        this.values.tenant = '';
        this.values.twoFAExemption = false;
		this.values.accessToUi = true;

        this.userNameValidationMessage = '';
        this.firstNameValidationMessage = '';
        this.lastNameValidationMessage = '';
        this.emailValidationMessage = '';
        this.passwordValidationMessage = '';
        this.passwordConfirmedValidationMessage = '';
        this.registerErrorMessage = '';

        this.addUserErrorMessage = '';
    }

    getUserModel() {

        let user = {
            userName: this.values.userName,
            firstName: this.values.firstName,
            lastName: this.values.lastName,
            email: this.values.email,
            password: this.values.password,
            roleIds: this.values.roleIds,
            teamIds: this.values.teamIds,
            phoneNumber: this.values.phoneNumber,
            cellPhoneNumber: this.values.cellPhoneNumber,
            jobTitle: this.values.jobTitle,
            other: this.values.other,
            country: this.values.country,
            active: this.values.active,
            allowedIpList: this.values.allowedIpList,
            authenticationProviderId: this.values.authenticationProviderId,
            tenant: this.values.tenant
        };

        if (this.values.expirationDate) {
            user.expirationDate = this.values.expirationDate;
        }

        user.localeId = this.values.localeId || 1;

        return user;
    }

    @action createUser() {

        this.creatingUser = true;
        this.errors = null;
        this.isAddUserError = false;
        this.addUserErrorMessage = '';

        const user = this.getUserModel();

        return agent.Users.createUser(user)
            .then(() => {
                
                this.creatingUser = false;
                this.reset();
            })
            .catch(action((err) => {

                this.creatingUser = false;

                if (err && err.response && err.response.text) {
    
                    this.isAddUserError = true;
                    this.addUserErrorMessage = JSON.parse(err.response.text).Message;
                }

                throw err;
            }))
            .finally(() => {
                this.creatingUser = false;
            });
    }

    @action updateTwoFAExemptionStatus(id, userExempted) {
        return agent.Users.updateUserExemption(id, userExempted);
    }
	
	@action updateAccessToUi(id, userHasAccess) {
		return agent.Users.updateUserAccessToUi(id, userHasAccess);
	}
	
    @action updateUser() {

        this.updatingUser = true;
        this.errors = null;
        this.isAddUserError = false;
        this.addUserErrorMessage = '';

        const user = this.getUserModel();
        user.id = this.values.id;
        
        return agent.Users.updateUser(user.id, user)
            .then(() => {
                
                let curretnUserId = oidcStore.currentUser.profile.sub;
                if(curretnUserId == user.id){

                    oidcStore.startSilentRenew().catch((err)=> {
                        Console.log('startSilentRenew failed:' + err);
                    });
                }

                this.updatingUser = false;
                this.reset();
            })
            .catch(action((err) => {

                this.updatingUser = false;
                
                if (err && err.response && err.response.text) {
    
                    this.isAddUserError = true;
                    this.addUserErrorMessage = JSON.parse(err.response.text).Message;
                }

                throw err;
            }))
            .finally(() => {
                this.updatingUser = false;
            });
    }

    @action setUserId(id) {
        this.values.id = id;
    }

    @action setFirstName(firstName) {
        this.values.firstName = firstName;
    }

    @action setLastName(lastName) {
        this.values.lastName = lastName;
    }

    @action setUserName(userName) {
        this.values.userName = userName.trim();
    }

    @action setEmail(email) {
        this.values.email = email.trim();
    }

    @action setPassword(password) {
        this.values.password = password;
    }

    @action setJobTitle(jobTitle) {
        this.values.jobTitle = jobTitle;
    }

    @action setConfirmedPassword(confirmedPassword) {
        this.values.confirmedPassword = confirmedPassword;
    }

    @action setPhoneNumber(phoneNumber) {
        this.values.phoneNumber = phoneNumber;
    }

    @action setCellPhoneNumber(cellPhoneNumber) {
        this.values.cellPhoneNumber = cellPhoneNumber;
    }

    @action setOther(other) {
        this.values.other = other;
    }

    @action setCountry(country) {
        this.values.country = country;
    }

    @action setActive(active) {
        this.values.active = active;
    }

    @action setExpirationDate(expirationDate) {
        this.values.expirationDate = expirationDate;
    }

    @action setAllowedIpList(allowedIpList) {
        this.values.allowedIpList = allowedIpList;
    }

    @action setLocaleId(localeId) {
        this.values.localeId = localeId;
    }

    @action setRoleIds(roleIds) {
        this.values.roleIds = roleIds;
    }

    @action setRoleId(roleId) {

        this.values.roleIds.push(roleId);
    }

    @action removeRoleId(roleId) {

        const index = this.values.roleIds.indexOf(roleId);

        if (index > -1) {
            this.values.roleIds.splice(index, 1);
        }
    }

    @action setTeamIds(teamIds) {

        this.values.teamIds = teamIds;
    }

    @action setTenant(tenant) {

        this.values.tenant = tenant;
    }

    @action setTwoFAExemption(val) {
        this.values.twoFAExemption = val;
    }
	
	@action setAccessToUi(val) {
		this.values.accessToUi = val;
	}

    @action setFormMode(formMode) {

        this.formMode = formMode;
    }

    @action setIsAddUserError(isError) {

        this.isAddUserError = isError;
    }

    @action setAddUserErrorMessage(errorMessage) {

        this.addUserErrorMessage = errorMessage;
    }

    @action setAuthenticationProviderType(authenticationProviderType) {

        this.values.authenticationProviderType = authenticationProviderType;
    }

    @action setAuthenticationProviderFormatted(authenticationProviderFormatted) {

        this.values.authenticationProviderFormatted = authenticationProviderFormatted;
    }

    @action setAuthenticationProviderId(authenticationProviderId) {

        this.values.authenticationProviderId = authenticationProviderId;
    }

    @action setDefaultAuthenticationProviderType() {

        this.setAuthenticationProviderType(AuthenticationProviderType.Application);
    }

    @action isNotLocalAuthenticationType() {

        if (this.values.authenticationProviderType === '') {
            
            return false;
        }

        return this.values.authenticationProviderType.toLowerCase() 
                    !== AuthenticationProviderType.Application.toLowerCase();
    }

    @action isLocalAuthenticationType() {

        return this.values.authenticationProviderType.toLowerCase() 
                    === AuthenticationProviderType.Application.toLowerCase();
    }

    calulateDefaultExpirationDate() {

        const date = new Date();
        const year = date.getFullYear();
        const month = date.getMonth();
        const day = date.getDate();
        const newDate = new Date(year + 3, month, day);

        return newDate;
    }

    calulateMinValidDate() {

        const date = new Date();
        const year = date.getFullYear();
        const month = date.getMonth();
        const day = date.getDate();
        const newDate = new Date(year, month, day);

        return newDate;
    }
}

export default new AddEditUserStore();


// WEBPACK FOOTER //
// ./src/stores/addEditUserStore.js